<script>
import { mapActions, mapGetters } from 'vuex';
import store from '@/store';
import { find, cloneDeep, uniq } from 'lodash';
import { format } from 'date-fns';
// import { orderBy } from 'natural-orderby';
import ArticlesApi from '@/api/articles';

import { required, numeric, gt, and, withMessage, custom, revalidate, optional, async, andSequence } from 'form-validation-manager';

import DatesLivraisonMixin from '../utils/dates-livraisons.mixin';
import ValidationsLieuxMixin from '../utils/validation-lieux.mixin';
import StepManager from './step-manager';
import SelectModeCamion from './selection-mode-camion';
import InputQuantite from './input-quantite';
import InputStockage from './input-stockage';
import LibelleDatesLivraison from '../../../../components/libelle-dates-livraison';

function getOrDefault (fn, defaultvalue) {
  let value;
  try {
    value = fn();
  } catch (e) { }
  return value === undefined ? defaultvalue : value;
}

export default {
  name: 'LineEdit',
  components: {
    SelectModeCamion,
    InputQuantite,
    LibelleDatesLivraison,
    InputStockage
  },
  mixins: [
    DatesLivraisonMixin,
    ValidationsLieuxMixin
  ],
  props: {
    line: {
      type: Object,
      default: null
    },
    isAjout: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    loading: true,
    contraintesLivraison: {},
    formConstraint: {
      lockQuantite: false
    },
    form: {
      produit: {
        article: null,
        quantite: null,
        codeFamilleArticle: null,
        codeReglement: null,
        codeDepartement: null
      },
      supplementations: {
        supplementations: null,
        mineraux: null
      },
      stockage: {
        adresse: null,
        batiment: null,
        lieu1: null,
        lieu2: null,
        lieu3: null
      },
      livraison: {
        dateDebut: null,
        dateFin: null,
        horaireDebut: null,
        horaireFin: null,
        modeCamion: null,
        commentaire: null
      }
    },
    formSteps: null,
    etatZoneCommentaire: false,
    displayHoraires: true,
    displaySupp: false,
    displayMineraux: false,
    supplementations: null,
    mineraux: null
  }),
  // ██╗   ██╗ █████╗ ██╗     ██╗██████╗  █████╗ ████████╗██╗ ██████╗ ███╗   ██╗███████╗
  // ██║   ██║██╔══██╗██║     ██║██╔══██╗██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║██╔════╝
  // ██║   ██║███████║██║     ██║██║  ██║███████║   ██║   ██║██║   ██║██╔██╗ ██║███████╗
  // ╚██╗ ██╔╝██╔══██║██║     ██║██║  ██║██╔══██║   ██║   ██║██║   ██║██║╚██╗██║╚════██║
  //  ╚████╔╝ ██║  ██║███████╗██║██████╔╝██║  ██║   ██║   ██║╚██████╔╝██║ ╚████║███████║
  //   ╚═══╝  ╚═╝  ╚═╝╚══════╝╚═╝╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝
  validations: {
    form: {
      produit: {
        quantite: and(
          withMessage(and(required(), numeric(), gt(0)), 'Veuillez renseigner une quantité valide'),
          custom(function (value) {
            if (this.minQuantite >= this.maxQuantite) {
              if (value !== this.minQuantite) {
                return 'Veuillez renseigner une valeur égale à ' + this.minQuantite + ' t';
              }
            }

            // if (value < this.minQuantite) {
            //   return 'Veuillez renseigner une valeur supérieure ou égale à ' + this.minQuantite + ' t';
            // }

            if (value > this.maxQuantite) {
              return 'Veuillez renseigner une valeur inférieure ou égale à ' + this.maxQuantite + ' t';
            }

            return false;
          }),
          revalidate('form.livraison.modeCamion')
        )
      },
      supplementations: {
        supplementations: and(
          custom(function (supps) {
            let codeStArt = supps.map(s => s.codeSousTypeArticle);
            codeStArt = uniq(codeStArt);
            if (codeStArt.length > 1) {
              return 'Supplémentations incompatibles';
            }
            return false;
          }),
          custom(function (supps) {
            if (supps.length > this.maxSupp) {
              return 'max ' + this.maxSupp + ' supplémentation(s) de ce type pour cet article';
            }
            return false;
          })
        ),
        mineraux: {
          $each: {
            quantiteCommande: and(
              withMessage(and(required(), numeric(), gt(0)), 'Veuillez renseigner une quantité de mineraux voulue'),
              custom(function (value) {
                if (value > this.paramsCommande.contraintesArticle.quantiteMaxMineral) {
                  return 'Veuillez renseigner une valeur maximale ou égale à ' + this.paramsCommande.contraintesArticle.quantiteMaxMineral + ' %';
                }

                return false;
              })
            )
          }
        }
      },
      stockage: {
        adresse: andSequence(
          withMessage(required(), 'Veuillez renseigner une adresse de livraison'),
          async(function (val) {
            return this.validerAdresseLivraison({ numArticle: this.line.numArticle, numTiers: this.numTiers, numAdresse: val })
              .then(response =>
                response
                  ? false
                  : 'Le cahier des charges de l\'adresse n\'est pas compatible avec le l\'article'
              );
          })
        ),
        batiment: optional(withMessage(numeric(), 'Doit être une valeur numérique, ou vide')),
        lieu1: withMessage(required(), 'Veuillez renseigner au moins un lieu de livraison')
      },
      livraison: {
        dateDebut: and(
          withMessage(required(), 'Veuillez renseigner la période de livraison voulue'),
          custom(function (value) {
            return this.dateValidator('DATE_DEBUT', value);
          }),
          revalidate('form.livraison.dateFin'),
          revalidate('form.stockage'),
          revalidate('form.livraison.horaireDebut'),
          revalidate('form.livraison.horaireFin')
        ),
        dateFin: and(
          withMessage(required(), 'Veuillez renseigner la période de livraison voulue'),
          custom(function (value) {
            return this.dateValidator('DATE_FIN', value);
          }),
          revalidate('form.stockage'),
          revalidate('form.livraison.horaireFin')
        ),
        horaireDebut: and(
          withMessage(required(), 'Requis'),
          custom(function (value) {
            if (this.validationHoraireDebut(value) === false) {
              return 'L\'horaire de début de livraison doit etre identique pour tous les aliments dont la date de début de livraison est la même';
            }
          }),
          revalidate('form.livraison.horaireFin')),
        horaireFin: and(
          required(),
          withMessage(custom(function (value) {
            // eslint-disable-next-line eqeqeq
            if (this.form.livraison.dateDebut == this.form.livraison.dateFin) {
              const horaires = this.form.livraison.horaireDebut + '-' + this.form.livraison.horaireFin;
              if (~['JS-JM', 'JS-JA', 'JS-JJ', 'JS-J1', 'JA-JM', 'JA-J1', 'JM-J1', 'JJ-J1'].indexOf(horaires)) {
                return true;
              }
            }
            return false;
          }), 'Les horaires ne sont pas valides pour une livraison sur un seul jour')
        ),
        modeCamion: and(
          required(),
          custom(function (value) {
            const mode = find(this.ref.modesCamion, { code: value });
            if (mode) {
              if (this.form.produit.quantite < mode.minQuantite) {
                return 'Le mode camion sélectionné n’est pas compatible avec la quantité saisie : minimum ' + mode.minQuantite + 't';
              }

              if (this.form.produit.quantite > mode.maxQuantite) {
                return 'Le mode camion sélectionné n’est pas compatible avec la quantité saisie : maximum ' + mode.maxQuantite + 't';
              }
            }
          }),
          revalidate('form.livraison.dateDebut'),
          revalidate('form.livraison.dateFin')
        )
      }
    }
  },
  //  ██████╗ ██████╗ ███╗   ███╗██████╗ ██╗   ██╗████████╗███████╗██████╗
  // ██╔════╝██╔═══██╗████╗ ████║██╔══██╗██║   ██║╚══██╔══╝██╔════╝██╔══██╗
  // ██║     ██║   ██║██╔████╔██║██████╔╝██║   ██║   ██║   █████╗  ██║  ██║
  // ██║     ██║   ██║██║╚██╔╝██║██╔═══╝ ██║   ██║   ██║   ██╔══╝  ██║  ██║
  // ╚██████╗╚██████╔╝██║ ╚═╝ ██║██║     ╚██████╔╝   ██║   ███████╗██████╔╝
  //  ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝      ╚═════╝    ╚═╝   ╚══════╝╚═════╝
  computed: {
    ...mapGetters({
      paramsCommande: 'referentiel/paramsCommande',
      ref: 'referentiel/referentiel',
      _batiments: 'exploitation/batiments',
      numTiers: 'user/numTiers',
      datesLivraisonDefaut: 'cart/datesLivraisonDefaut',
      periodesLivraisonDefaut: 'cart/periodesLivraisonDefaut',
      panier: 'cart/cart',
      adresseLivraisonValide: 'articles/adresseLivraisonValide'
    }),
    adresses () {
      return this.ref.adresses.filter(a => a.etat !== 'S').map(a => a.numAdr);
    },
    batiments () {
      return this._batiments.map(b => b.numBatiment);
    },
    periodesLivraison () {
      return this.ref.periodesLivraison.map(item => ({ value: item.code, text: item.libelle }));
    },
    zoneCommentaireVisible () {
      return this.etatZoneCommentaire || this.form.livraison.commentaire;
    },
    minQuantite () {
      const min = [0];

      if (this.paramsCommande.contraintesArticle) {
        if (this.paramsCommande.contraintesArticle.quantiteMin !== undefined && this.paramsCommande.contraintesArticle.quantiteMin !== null) {
          min.push(this.paramsCommande.contraintesArticle.quantiteMin);
        }
        if (this.min !== null && this.min !== undefined) {
          min.push(this.min);
        }
      }

      const v = Math.max(...min);

      return Math.round(v * 1000) / 1000;
    },
    maxQuantite () {
      const max = [29];

      if (this.paramsCommande.contraintesArticle) {
        if (this.paramsCommande.contraintesArticle.quantiteMax !== undefined && this.paramsCommande.contraintesArticle.quantiteMax !== null) {
          max.push(this.paramsCommande.contraintesArticle.quantiteMax);
        }
        if (this.max !== null && this.max !== undefined) {
          max.push(this.max);
        }
      }

      const v = Math.min(...max);
      return Math.round(v * 1000) / 1000;
    },
    nbJoursEcartMinDatesLivraison () {
      let nbJoursEcartMin = 0;
      if ((this.form.produit.codeFamilleArticle === '461') || this.form.livraison.modeCamion === 'VISP' || this.form.livraison.modeCamion === 'PNEUP') {
        nbJoursEcartMin = 1;
      }

      return nbJoursEcartMin;
    },
    batimentReadOnly () {
      let val = false;
      if (this.line && !!this.line.numLot) { val = false; };
      if (this.line && this.line.numLot) { val = true; };
      return val;
    },
    batimentDisplay () {
      let val = true;

      if (this.line.codeDepartement && this.line.codeDepartement !== '45') { val = false; };
      if (this.line.codeFiliere && this.line.codeFiliere !== '60' && this.line.codeFiliere !== '67') { val = false; }
      return val;
    },
    maxSupp () {
      if (this.form.supplementations.supplementations) {
        for (const supp of this.form.supplementations.supplementations) {
          if (supp.codeSousTypeArticle === 'AA') {
            return 1;
          }
        }
      }
      return Infinity;
    },
    supplementationsPossibles () {
      let hasAA = false;
      let hasSU = false;
      if (this.form.supplementations.supplementations) {
        for (const supp of this.form.supplementations.supplementations) {
          if (supp.codeSousTypeArticle === 'AA') {
            hasAA = true;
          }
          if (supp.codeSousTypeArticle === 'SU') {
            hasSU = true;
          }
        }

        if (hasAA) {
          return this.supplementations.filter(s => s.codeSousTypeArticle === 'AA');
        }
        if (hasSU) {
          return this.supplementations.filter(s => s.codeSousTypeArticle === 'SU');
        }
      }

      return this.supplementations;
    }
  },
  // ██╗    ██╗ █████╗ ████████╗ ██████╗██╗  ██╗
  // ██║    ██║██╔══██╗╚══██╔══╝██╔════╝██║  ██║
  // ██║ █╗ ██║███████║   ██║   ██║     ███████║
  // ██║███╗██║██╔══██║   ██║   ██║     ██╔══██║
  // ╚███╔███╔╝██║  ██║   ██║   ╚██████╗██║  ██║
  //  ╚══╝╚══╝ ╚═╝  ╚═╝   ╚═╝    ╚═════╝╚═╝  ╚═╝
  watch: {
    isMobile () {
      this.initFormStep();
    },
    'form.stockage.batiment' (val) {
      if (typeof val === 'string') {
        this.form.stockage.batiment = parseInt(val);
      }
    },
    'form.stockage.adresse' () {
      this.getContraintesLivraison();
    },
    'form.livraison.dateDebut' (date) {
      if (!date) {
        return;
      }
      if (!this.form.livraison.dateFin) {
        const dateFin = this.$dateValidation.calculDateDefaut('DATE_FIN', date, null, 1, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison);
        this.form.livraison.dateFin = format(dateFin, 'yyyy-MM-dd');
      }

      this.dateCmdAnticipee(date);
    },
    'form.livraison.dateFin' (date) {
      if (!date) {
        return;
      }
      if (!this.form.livraison.dateDebut) {
        const dateDebut = this.$dateValidation.calculDateDefaut('DATE_DEBUT', null, date, 1, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison);
        this.form.livraison.dateDebut = format(dateDebut, 'yyyy-MM-dd');
      }

      if (this.form.livraison.dateDebut && this.form.livraison.dateFin) {
        this.ctrlIntervalleDateCmd(this.form.livraison.dateDebut, this.form.livraison.dateFin);
      }
    },
    'form.supplementations.mineraux' (mineraux) {
      setTimeout(() => {
        const node = document.querySelector('#mineral-' + (mineraux.length - 1) + ' input');
        if (node && node.value === '') {
          node.focus();
        }
      });
    }
  },
  //  ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗██████╗
  // ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝██╔══██╗
  // ██║     ██████╔╝█████╗  ███████║   ██║   █████╗  ██║  ██║
  // ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝  ██║  ██║
  // ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗██████╔╝
  //  ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝╚═════╝
  async created () {
    this.$ga.page({
      title: 'Ajout au panier',
      location: '/ajout-au-panier'
    });

    this.initFormStep();
    await Promise.all([ // await car on a besoin des données plus loin dans la fonction
      this.loadParamsCommande({ numTiers: this.numTiers, numArticle: this.line.numArticle, numLot: this.line.numLot }),
      this.loadBatiments(this.numTiers),
      ArticlesApi.getSupplementations(this.line.codeTypeArticle, this.line.numArticle, this.numTiers),
      ArticlesApi.getMineraux(this.line.codeTypeArticle, this.line.numArticle, this.numTiers),
      this.$dateValidationLoad(),
      this.$lieuxValidationLoad()
    ]).then(([params, batiments, supplementations, mineraux]) => {
      this.supplementations = supplementations.sort(s => s.libelleLong)
        .map(s => { s.groupe = s.libelleLong.replace(/\d+([.,]\d+)?/g, ''); return s; })
        .map(article => Object.assign({}, article, { libelleArticle: article.libelleLong }));
      this.mineraux = mineraux.map(article => ({ codeTypeArticle: article.codeTypeArticle, numArticle: article.numArticle, libelleArticle: article.libelle }));
    });

    // init form
    // produit
    this.form.produit.codeReglement = this.line.codeReglement;

    this.form.produit.article = this.line.libelleArticle;
    this.form.produit.codeFamilleArticle = this.line.codeFamilleArticle;
    this.form.produit.codeDepartement = this.line.codeDepartement;

    // supp et mineraux
    this.form.supplementations.supplementations = (cloneDeep(this.line.supplementations) || []).filter(s => find(this.supplementations, { numArticle: s.numArticle }));
    this.form.supplementations.mineraux = (cloneDeep(this.line.mineraux) || []).filter(m => find(this.mineraux, { numArticle: m.numArticle }));
    if (this.form.supplementations.supplementations && this.form.supplementations.supplementations.length) {
      this.displaySupp = true;
    }
    if (this.form.supplementations.mineraux && this.form.supplementations.mineraux.length) {
      this.displayMineraux = true;
    }
    // stockage
    this.form.stockage.batiment = this.line.numBatiment;
    this.line.numAdr = this.line.numAdr || this.line.numAdresse || this.line.numeroAdresse || this.line.numExploitation || this.line.numeroExploitation;
    this.form.stockage.adresse = this.adresses.indexOf(this.line.numAdr) !== -1 ? this.line.numAdr : null;
    if (this.form.stockage.adresse === null || this.form.stockage.adresse === undefined) {
      if (this.adresses.length === 1) {
        this.form.stockage.adresse = this.adresses[0];
      }
    }

    [this.form.stockage.lieu1, this.form.stockage.lieu2, this.form.stockage.lieu3] = [this.line.codeTypeSilo1, this.line.codeTypeSilo2, this.line.codeTypeSilo3]
      .map(lieu => (typeof lieu === 'string' && lieu.startsWith('997:') && !this.isAjout) || this.isTypeSiloValide(lieu) ? lieu : null)
      .filter(lieu => !!lieu);

    // livraison
    this.form.livraison.horaireDebut = this.line.matinApmidi || 'ID';
    this.form.livraison.horaireFin = this.line.finMatinApmidi || 'ID';

    this.form.livraison.modeCamion = this.line.codeModeCamion || this.ref.modesCamion[0].code;

    if (!this.isAjout) {
      // modification panier, on recupere toutes les données
      this.form.produit.quantite = this.line.quantiteCommande;

      this.form.livraison.dateDebut = this.line.datePrevueLivraison;
      this.form.livraison.dateFin = this.line.finDatePrevueLivraison;

      this.form.livraison.commentaire = this.line.commentaire;
    } else {
      // on garde les periodes deja selectionnees
      this.form.livraison.horaireDebut = getOrDefault(() => this.periodesLivraisonDefaut.horaireDebut, 'ID');
      this.form.livraison.horaireFin = getOrDefault(() => this.periodesLivraisonDefaut.horaireFin, 'ID');

      // ajout au panier, on selectionne les dates par defaut si il y a lieu
      if (this.datesLivraisonDefaut) {
        this.form.livraison.dateDebut = this.datesLivraisonDefaut.dateDebut || null;
        if (this.$dateValidation.validationDate('DATE_FIN', this.datesLivraisonDefaut.dateDebut, this.datesLivraisonDefaut.datefin, this.nbJoursEcartMinDatesLivraison) === null) {
          this.form.livraison.dateFin = this.datesLivraisonDefaut.dateFin || null;
        } else {
          this.form.livraison.dateFin = format(this.$dateValidation.calculDateDefaut('DATE_FIN', this.form.livraison.dateDebut, null, 1), 'yyyy-MM-dd');
        }
      }
    }

    this.etatZoneCommentaire = this.zoneCommentaireVisible;

    // this.displayHoraires = this.form.livraison.horaireDebut !== 'ID' || this.form.livraison.horaireFin !== 'ID';

    if (this.minQuantite >= this.maxQuantite) {
      this.form.produit.quantite = this.minQuantite;
      this.formConstraint.lockQuantite = true;
    }

    await this.getContraintesLivraison();
    this.loading = false;

    this.$fvm.$events.on('done', (field) => {
      if (field.path === 'form.stockage.adresse') {
        setTimeout(() => this.$refs.adresse.validate(true));
      }
    });
  },
  // ███████╗██╗   ██╗███████╗███╗   ██╗████████╗███████╗
  // ██╔════╝██║   ██║██╔════╝████╗  ██║╚══██╔══╝██╔════╝
  // █████╗  ██║   ██║█████╗  ██╔██╗ ██║   ██║   ███████╗
  // ██╔══╝  ╚██╗ ██╔╝██╔══╝  ██║╚██╗██║   ██║   ╚════██║
  // ███████╗ ╚████╔╝ ███████╗██║ ╚████║   ██║   ███████║
  // ╚══════╝  ╚═══╝  ╚══════╝╚═╝  ╚═══╝   ╚═╝   ╚══════╝
  mounted () {
    this.unregisterRouteGuard = this.$router.beforeEach((to, from, next) => {
      this.displayCloseConfirm() // call cancel confirm dialog
        .then(confirm => {
          if (confirm) {
            // execute navigation event
            next();
          } else {
            // canceled
            next(false); // cancel navigation event
            store.dispatch('navigation/end'); // clear loader (binded on navigations events)
          }
        });
    });
  },
  beforeDestroy () {
    this.unregisterRouteGuard();
  },
  // ███╗   ███╗███████╗████████╗██╗  ██╗ ██████╗ ██████╗ ███████╗
  // ████╗ ████║██╔════╝╚══██╔══╝██║  ██║██╔═══██╗██╔══██╗██╔════╝
  // ██╔████╔██║█████╗     ██║   ███████║██║   ██║██║  ██║███████╗
  // ██║╚██╔╝██║██╔══╝     ██║   ██╔══██║██║   ██║██║  ██║╚════██║
  // ██║ ╚═╝ ██║███████╗   ██║   ██║  ██║╚██████╔╝██████╔╝███████║
  // ╚═╝     ╚═╝╚══════╝   ╚═╝   ╚═╝  ╚═╝ ╚═════╝ ╚═════╝ ╚══════╝
  methods: {
    ...mapActions({ loadBatiments: 'exploitation/loadBatiments', loadParamsCommande: 'referentiel/loadParamsCommande', validerAdresseLivraison: 'articles/loadAdresseLivraisonValide' }),
    initFormStep () {
      if (this.isMobile) {
        this.formSteps = new StepManager(['produit', 'supplementations', 'stockage'], 'livraison');
      } else {
        this.formSteps = new StepManager(['produit', 'supplementations', 'stockage', 'livraison']);
      }
    },
    displayCloseConfirm () {
      return this.$dialog.confirm({
        title: 'Annuler ?',
        text: 'Voulez-vous vraiment annuler vos modifications?',
        actions: {
          true: {
            text: 'Oui',
            flat: false,
            outlined: true
          },
          false: {
            text: 'Non',
            flat: false
          }
        }
      });
    },
    cancel () {
      this.displayCloseConfirm().then(confirm => {
        if (confirm) {
          this.$emit('cancel');
          this.$ga.page(this.$router);
        }
      });
    },
    validation () {
      setTimeout(() => this.submit(), 50);
    },
    submit () {
      this.$refs.form.validate();
      if (this.$fvm.$isValid) {
        const line = Object.assign({}, this.line);

        line.quantiteCommande = typeof this.form.produit.quantite === 'string' ? parseFloat(this.form.produit.quantite.replace(',', '.')) : this.form.produit.quantite;
        line.supplementations = this.form.supplementations.supplementations;
        line.mineraux = this.form.supplementations.mineraux;
        line.supplementations.forEach(i => { i.codeUniteArticle = '%'; });
        line.mineraux.forEach(i => { i.codeUniteArticle = '%'; });
        if (!(line.supplementations && line.supplementations.length)) {
          delete line.supplementations;
        }
        if (!(line.mineraux && line.mineraux.length)) {
          delete line.mineraux;
        }

        line.numBatiment = this.form.stockage.batiment;
        line.numAdresse = this.form.stockage.adresse;
        line.codeTypeSilo1 = this.form.stockage.lieu1;
        line.codeTypeSilo2 = this.form.stockage.lieu2;
        line.codeTypeSilo3 = this.form.stockage.lieu3;

        line.datePrevueLivraison = this.form.livraison.dateDebut;
        line.finDatePrevueLivraison = this.form.livraison.dateFin;
        line.matinApmidi = this.form.livraison.horaireDebut;
        line.finMatinApmidi = this.form.livraison.horaireFin;
        line.commentaire = this.form.livraison.commentaire;

        line.codeModeCamion = this.form.livraison.modeCamion;

        line.codeReglement = this.form.produit.codeReglement;

        this.$emit('validate', line);
        this.$ga.page(this.$router);
      }
    },
    getSilos (numBatiment) {
      numBatiment = numBatiment || 99;
      const batiment = find(this._batiments, { numBatiment });
      if (batiment) {
        return batiment.typesSilo;
      } else {
        return [];
      }
    },
    isTypeSiloValide (code) {
      return !!find(this.paramsCommande.typesSiloDefault, { code });
    },
    previousStep () {
      this.formSteps.previousStep();
    },
    nextStep () {
      let formParts = this.formSteps.getStep();
      if (!Array.isArray(formParts)) {
        formParts = [formParts];
      }

      let invalid = false;
      for (let formPart of formParts) {
        if (this.$fvm.form[formPart].$error) {
          this.$refs.form.validate();
          invalid = true;
        }
      }

      if (invalid) {
        return;
      }
      this.formSteps.nextStep();
    },
    dateValidator (key, date) {
      const dateDebut = key === 'DATE_DEBUT' ? date : this.form.livraison.dateDebut;
      const dateFin = key === 'DATE_FIN' ? date : this.form.livraison.dateFin;

      const modeCamion = find(this.ref.modesCamion, { code: this.form.livraison.modeCamion });
      const delaisMinJoursOuvres = Math.max(
        this.contraintesLivraison.delaisMinJoursOuvres,
        modeCamion && modeCamion.delaiMinLivraison ? modeCamion.delaiMinLivraison : 0
      );

      return this.$dateValidation.validationDate(key, dateDebut, dateFin,
        this.nbJoursEcartMinDatesLivraison,
        delaisMinJoursOuvres,
        this.contraintesLivraison);
    },

    dateCmdAnticipee (date) {
      if (this.$dateValidation.validationDelais(date)) {
        this.$dialog
          .confirm({
            title: 'Période anticipée',
            text: 'La date début de période de livraison est anticipée. Voulez-vous continuer ? ',
            actions: { false: { text: 'Annuler', flat: false }, true: { text: 'Continuer', flat: false, outlined: true } }
          })
          .then(confirm => {
            if (!confirm) {
              this.form.livraison.dateDebut = null;
            }
          });
      }
    },

    ctrlIntervalleDateCmd (dateDebut, dateFin) {
      if (this.$dateValidation.validationIntervalle(dateDebut, dateFin)) {
        this.$dialog
          .confirm({
            title: 'Intervalle de date',
            text: 'L\'intervalle  entre les deux dates de livraison est au-delà de 5 jours. Voulez-vous continuer ? ',
            actions: { true: { text: 'Continuer', flat: false, outlined: true }, false: { text: 'Annuler', flat: false } }
          })
          .then(confirm => {
            if (!confirm) {
              this.form.livraison.dateFin = null;
            }
          });
      }
    },

    isDateAllowed (key) {
      return (date) => {
        return this.dateValidator(key, date) == null;
      };
    },
    afficherZoneCommentaire () {
      this.etatZoneCommentaire = true;
    },
    isSupplementationDisabled (supp) {
      return !!find(this.form.supplementations.supplementations, { groupe: supp.groupe });
    },
    validationStockage (lieu) {
      const ligne = {
        codeTypeSilo1: lieu,
        datePrevueLivraison: this.form.livraison.dateDebut,
        finDatePrevueLivraison: this.form.livraison.dateFin,
        idLigne: this.line ? this.line.idLigne : undefined,
        numBatiment: this.form.stockage.batiment,
        numAdresse: this.form.stockage.adresse
      };
      const err = this.$lieuxValidation.validationLivraison(ligne);
      return err.alert || err.error ? ['Ce lieu de stockage est déjà prévu pour une autre commande de votre panier à la même date'] : '';
    },
    validationHoraireDebut (code) {
      if (code === 'ID') {
        return true;
      }
      for (const ligne of this.panier) {
        if (ligne.idLigne === this.line.idLigne) {
          // on, ne verifie pas la ligne par rapport a elle meme
          continue;
        }
        if (ligne.datePrevueLivraison === this.form.livraison.dateDebut && ligne.matinApmidi !== 'ID' && code !== ligne.matinApmidi) {
          return false;
        }
      }

      return true;
    },

    async getContraintesLivraison () {
      if (!this.form.stockage.adresse) {
        this.contraintesLivraison = {};
        return Promise.resolve();
      } else {
        return ArticlesApi.getContraintesLivraison(this.line.codeTypeArticle, this.line.numArticle, this.numTiers, this.form.stockage.adresse).then(
          contraintes => {
            this.contraintesLivraison = contraintes;
            this.$fvm.form.livraison.validate();
          }
        );
      }
    }
  }

};
</script>

// ████████╗███████╗███╗   ███╗██████╗ ██╗      █████╗ ████████╗███████╗
// ╚══██╔══╝██╔════╝████╗ ████║██╔══██╗██║     ██╔══██╗╚══██╔══╝██╔════╝
//    ██║   █████╗  ██╔████╔██║██████╔╝██║     ███████║   ██║   █████╗
//    ██║   ██╔══╝  ██║╚██╔╝██║██╔═══╝ ██║     ██╔══██║   ██║   ██╔══╝
//    ██║   ███████╗██║ ╚═╝ ██║██║     ███████╗██║  ██║   ██║   ███████╗
//    ╚═╝   ╚══════╝╚═╝     ╚═╝╚═╝     ╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

<template>
  <Loader v-if="loading" />
  <div
    v-else
    class="content"
    :class="isMobile ? 'screen-size' : ''"
  >
    <div>
      <h1
        v-if="isAjout"
        class="primary--text"
      >
        Ajouter au panier
        <CardsHelp
          :page="'AJOUT-MODIF-PANIER'"
          :niveau="'TITRE'"
        />
      </h1>
      <h1
        v-if="!isAjout"
        class="primary--text"
      >
        Modifier la commande
        <CardsHelp
          :page="'AJOUT-MODIF-PANIER'"
          :niveau="'TITRE'"
        />
      </h1>
      <v-form ref="form">
        <!--
██████╗ ██████╗  ██████╗ ██████╗ ██╗   ██╗██╗████████╗
██╔══██╗██╔══██╗██╔═══██╗██╔══██╗██║   ██║██║╚══██╔══╝
██████╔╝██████╔╝██║   ██║██║  ██║██║   ██║██║   ██║
██╔═══╝ ██╔══██╗██║   ██║██║  ██║██║   ██║██║   ██║
██║     ██║  ██║╚██████╔╝██████╔╝╚██████╔╝██║   ██║
╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═════╝  ╚═════╝ ╚═╝   ╚═╝
-->

        <div
          v-if="formSteps.isActive('produit')"
          class="form-group"
        >
          <h4 class="secondary--text">
            Produit
            <CardsHelp
              :page="'SUPPLEMENTATION'"
              :niveau="'SOUS-TITRE'"
            />
          </h4>
          <div class="form-row">
            <div>
              <strong>{{ form.produit.article }}</strong><sub class="num-article">{{ line.numArticle }}</sub>

              <div
                v-if="!displaySupp && supplementations && supplementations.length"
                class="btn-add button pointer"
                @click="displaySupp = true"
              >
                + Ajouter une supplémentation
              </div>
              <div
                v-if="!displayMineraux && mineraux && mineraux.length"
                class="btn-add button pointer"
                @click="displayMineraux = true"
              >
                + Ajouter un minéral
              </div>
            </div>
            <InputQuantite
              v-model="form.produit.quantite"
              :rules="$fvm.form.produit.quantite.$errors"
              :disabled="formConstraint.lockQuantite"
              :min-quantite="0"
              :max-quantite="Math.max(minQuantite, maxQuantite)"
              :step="0.5"
            />
          </div>
          <v-input :messages="$fvm.form.livraison.modeCamion.$errors">
            <template v-slot:message="{message}">
              <span class="display-inline-block warning--text">{{ message }}</span>
            </template>
          </v-input>
        </div>
        <!--
███████╗██╗   ██╗██████╗ ██████╗
██╔════╝██║   ██║██╔══██╗██╔══██╗
███████╗██║   ██║██████╔╝██████╔╝
╚════██║██║   ██║██╔═══╝ ██╔═══╝
███████║╚██████╔╝██║     ██║
╚══════╝ ╚═════╝ ╚═╝     ╚═╝
         -->
        <div v-if="formSteps.isActive('supplementations')">
          <div
            v-if="displaySupp || displayMineraux"
            class="form-group"
          >
            <h4 class="secondary--text">
              Supplémentations et minéraux
            </h4>
            <div class="form-row">
              <SelectItems
                v-if="displaySupp"
                v-model="form.supplementations.supplementations"
                :items="supplementationsPossibles"
                label="Supplementations"
                item-text="libelleArticle"
                :item-disabled="isSupplementationDisabled"
                track-by="numArticle"
                :max="maxSupp"
              />
              <SelectItems
                v-if="displayMineraux"
                v-model="form.supplementations.mineraux"
                :items="mineraux"
                label="Mineraux"
                item-text="libelleArticle"
                track-by="numArticle"
              >
                <template v-slot:selected-item-content="{item, index}">
                  <div class="mineral-selected">
                    {{ item.libelleArticle }}
                    <div class="mineral-qte">
                      <NumberField
                        :id="'mineral-'+index"
                        v-model="item.quantiteCommande"
                        outlined
                        hide-details
                        validate-on-blur
                        dense
                        type="number"
                        :rules="$fvm.form.supplementations.mineraux.$each[index].quantiteCommande.$errors"
                      />%
                    </div>
                  </div>
                  <div
                    v-if="$fvm.form.supplementations.mineraux.$each[index].quantiteCommande.$error"
                    class="error--text"
                    style="font-size:0.8em;"
                  >
                    {{ $fvm.form.supplementations.mineraux.$each[index].quantiteCommande.$errors.join(',') }}
                  </div>
                </template>
              </SelectItems>
            </div>
          </div>
        </div>
        <!--
███████╗████████╗ ██████╗  ██████╗██╗  ██╗ █████╗  ██████╗ ███████╗
██╔════╝╚══██╔══╝██╔═══██╗██╔════╝██║ ██╔╝██╔══██╗██╔════╝ ██╔════╝
███████╗   ██║   ██║   ██║██║     █████╔╝ ███████║██║  ███╗█████╗
╚════██║   ██║   ██║   ██║██║     ██╔═██╗ ██╔══██║██║   ██║██╔══╝
███████║   ██║   ╚██████╔╝╚██████╗██║  ██╗██║  ██║╚██████╔╝███████╗
╚══════╝   ╚═╝    ╚═════╝  ╚═════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚══════╝
         -->
        <div
          v-if="formSteps.isActive('stockage')"
          class="form-group"
        >
          <h4 class="secondary--text">
            Stockage
            <CardsHelp
              :page="'STOCKAGE'"
              :niveau="'SOUS-TITRE'"
            />
          </h4>
          <div class="form-row">
            <div id="adresses-list_aap">
              <v-select
                ref="adresse"
                v-model="form.stockage.adresse"
                attach="#adresses-list_aap"
                :items="adresses"
                label="Adresse de livraison *"
                prepend-icon="mdi-warehouse"
                :disabled="batimentReadOnly"
                :rules="$fvm.form.stockage.adresse.$errors"
              >
                <template v-slot:selection="{item}">
                  <RefValue
                    class="adresse-selection mt-2"
                    :code="item"
                    type="ADRESSE"
                  />
                </template>
                <template v-slot:item="{item}">
                  <span>•</span>
                  <RefValue
                    :code="item"
                    type="ADRESSE"
                  />
                </template>
              </v-select>
            </div>

            <v-combobox
              v-show="batimentDisplay"
              v-model="form.stockage.batiment"
              :items="batiments"
              label="Numéro de batiment"
              prepend-icon="mdi-warehouse"
              :disabled="batimentReadOnly"
              clearable
              :rules="$fvm.form.stockage.batiment.$errors"
              @change="form.stockage.lieu1=form.stockage.lieu2=form.stockage.lieu3=null;"
            />
          </div>
          <div class="form-row">
            <InputStockage
              v-model="form.stockage.lieu1"
              :items="getSilos(form.stockage.batiment)"
              label="1er lieu de stockage *"
              clearable
              :rules="$fvm.form.stockage.lieu1.$errors"
              icon="mdi-home-floor-1"
              :messages="validationStockage(form.stockage.lieu1)"
            />
            <InputStockage
              v-if="(form.stockage.lieu1 || form.stockage.lieu2)"
              v-model="form.stockage.lieu2"
              :items="getSilos(form.stockage.batiment)"
              label="2nd lieu de stockage"
              clearable
              icon="mdi-home-floor-2"
              :messages="validationStockage(form.stockage.lieu2)"
            />
            <InputStockage
              v-if="(form.stockage.lieu2 || form.stockage.lieu3)"
              v-model="form.stockage.lieu3"
              :items="getSilos(form.stockage.batiment)"
              label="3ème lieu de stockage"
              clearable
              icon="mdi-home-floor-3"
              :messages="validationStockage(form.stockage.lieu3)"
            />
          </div>
        </div>
        <!--
██╗     ██╗██╗   ██╗██████╗  █████╗ ██╗███████╗ ██████╗ ███╗   ██╗
██║     ██║██║   ██║██╔══██╗██╔══██╗██║██╔════╝██╔═══██╗████╗  ██║
██║     ██║██║   ██║██████╔╝███████║██║███████╗██║   ██║██╔██╗ ██║
██║     ██║╚██╗ ██╔╝██╔══██╗██╔══██║██║╚════██║██║   ██║██║╚██╗██║
███████╗██║ ╚████╔╝ ██║  ██║██║  ██║██║███████║╚██████╔╝██║ ╚████║
╚══════╝╚═╝  ╚═══╝  ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝╚══════╝ ╚═════╝ ╚═╝  ╚═══╝
 -->
        <div
          v-if="formSteps.isActive('livraison')"
          class="form-group"
        >
          <h4 class="secondary--text">
            Livraison
            <CardsHelp
              :page="'LIVRAISON'"
              :niveau="'SOUS-TITRE'"
            />
          </h4>
          <div class="form-row">
            <DateField
              v-model="form.livraison.dateDebut"
              label="Date au plus tôt *"
              :allowed-dates="isDateAllowed('DATE_DEBUT')"
              :rules="$fvm.form.livraison.dateDebut.$errors"
              close-on-select
            />
            <v-select
              v-if="displayHoraires"
              v-model="form.livraison.horaireDebut"
              :items="periodesLivraison"
              label="Plage horaire"
              prepend-icon="mdi-clock-outline"
              :rules="$fvm.form.livraison.horaireDebut.$errors"
            />
          </div>
          <div class="form-row">
            <DateField
              v-model="form.livraison.dateFin"
              label="Date au plus tard *"
              :allowed-dates="isDateAllowed('DATE_FIN')"
              :rules="$fvm.form.livraison.dateFin.$errors"
              close-on-select
            />
            <div
              v-if="!displayHoraires"
              class="btn-add"
              :class="{'mb-6':isMobile}"
              outlined
              color="grey"
              @click="displayHoraires = true"
            >
              + Ajouter des plages horaires
            </div>
            <v-select
              v-if="displayHoraires"
              v-model="form.livraison.horaireFin"
              :items="periodesLivraison"
              label="Plage horaire"
              prepend-icon="mdi-clock-outline"
              :rules="$fvm.form.livraison.horaireFin.$errors"
            />
          </div>
          <div
            v-if="displayHoraires"
            class="libelle-horaires"
          >
            <i>
              <LibelleDatesLivraison
                v-if="form.livraison.dateDebut && form.livraison.dateFin"
                :date-debut="form.livraison.dateDebut"
                :date-fin="form.livraison.dateFin"
                :horaire-debut="form.livraison.horaireDebut"
                :horaire-fin="form.livraison.horaireFin"
              />
            </i>
          </div>

          <div class="form-row details-camion">
            <SelectModeCamion
              v-model="form.livraison.modeCamion"
              :rules="$fvm.form.livraison.modeCamion.$errors"
            />
          </div>
          <!-- <span>
            <CardsHelp
              :page="'COMMENTAIRE'"
              :niveau="'SOUS-TITRE'"
            />
          </span> -->
          <!-- <div
            v-if="!zoneCommentaireVisible"
            outlined
            class="pointer"
            color="grey"
            @click="afficherZoneCommentaire()"
          >
            + Ajouter un commentaire
          </div>

          <div
            v-else
            class="commentaires"
          >
            <strong>Commentaires : </strong>
            <div class="form-row">
              <v-textarea
                v-model="form.livraison.commentaire"
                maxlength="1000"
                background-color="#eee"
                label="Commentaire"
              />
            </div>
          </div> -->
        </div>
        <div
          style="color: #bbb; text-align: right;"
          class="form-group py-2 pt-2"
        >
          * : champs obligatoires
        </div>
        <!--
 █████╗  ██████╗████████╗██╗ ██████╗ ███╗   ██╗███████╗
██╔══██╗██╔════╝╚══██╔══╝██║██╔═══██╗████╗  ██║██╔════╝
███████║██║        ██║   ██║██║   ██║██╔██╗ ██║███████╗
██╔══██║██║        ██║   ██║██║   ██║██║╚██╗██║╚════██║
██║  ██║╚██████╗   ██║   ██║╚██████╔╝██║ ╚████║███████║
╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝
 -->
        <div class="form-group actions">
          <v-btn
            v-if="formSteps.isFirstStep()"
            outlined
            color="grey"
            @click="cancel()"
          >
            Annuler
          </v-btn>
          <v-btn
            v-else
            outlined
            color="grey"
            @click="previousStep()"
          >
            <v-icon>mdi-arrow-left</v-icon>Précédent
          </v-btn>
          <v-btn
            v-if="formSteps.isLastStep()"
            :loading="$fvm.$pending"
            @click="validation()"
          >
            {{ isAjout ? 'Ajouter au panier': 'Enregistrer' }}
          </v-btn>
          <v-btn
            v-else
            :loading="$fvm.$pending"
            @click="nextStep()"
          >
            Suivant <v-icon>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.form-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 53em;
  justify-content: space-between;
  align-items: flex-start;
  &::v-deep > * {
    width: 25em;
    max-width: 25em;
  }
}

.content.screen-size {
  overflow-y: scroll;
}

.content {
  background-color: #f4f8f9;
  height: 100%;
  padding: 1em;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.content > div,
.form-row,
.form-group {
  max-width: 100%;
}

.btn-add {
  // font-size: 0.9em;
  color: grey;
  cursor: pointer;
  padding: 0.3em 1em 0.3em 0;
}

.commentaires .v-input--checkbox {
  margin: 0;
}

.actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 2em;
  & > *:not(:last-child) {
    margin-right: 2em;
  }
}

h4 {
  margin-bottom: 1em;
}

@import "~vuetify/src/styles/settings/_variables";
@media #{map-get($display-breakpoints, 'xs-only')} {
  .form-row {
    width: 100%;

    &::v-deep > * {
      width: 100%;
      max-width: 100%;
    }
  }
}

.mineral-selected {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  .mineral-qte {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  & ::v-deep .v-input,
  &::v-deep .v-text-field {
    max-width: 4em;
    min-width: 0;
    margin: 0 0.2em 0 1em;
    .v-input__slot {
      min-height: 0 !important;
    }
  }
}
.adresse-selection {
  max-width: 99%;
  display: inline-block;
}

.libelle-horaires {
  margin: 0 0 2em 1em;
}

#adresses-list_aap {
  position: relative;
}
#adresses-list_aap ::v-deep .v-list-item {
  align-items: flex-start;
  & > span {
    margin-right: 10px;
  }
}
#adresses-list_aap ::v-deep .v-list-item:nth-child(2n) {
  background-color: #eee;
}
</style>
